import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { storageService } from "@/application/services/StorageService";
import { LoggerFactory } from "@/application/logging/Logger";
import { secureStorage } from "@/utils/secureStorage";
import { StripeRepository } from "@/infrastructure/repositories/StripeRepository";

interface PaymentCompleteProps {
  canceled?: boolean;
  onFinish: (goTo?: string) => void;
}

export function PaymentComplete({
  canceled,
  onFinish
}: PaymentCompleteProps) {
  const [status, setStatus] = useState<"success" | "failed" | null>(null);
  const logger = LoggerFactory.getLogger();
  const stripeRepository = useMemo(() => new StripeRepository(), []);

  useEffect(() => {
    const verifySubscription = async () => {
      const params = new URLSearchParams(window.location.search);
      const sessionId =
        params.get("session_id") ||
        secureStorage.get<string>("stripe_checkout_session_id");

      if (!sessionId) {
        setStatus("failed");
        toast.error("Invalid payment session.");
        return;
      }

      try {
        const data = await stripeRepository.getCheckoutSession(sessionId);
        if (!data) {
          throw new Error("Unable to fetch checkout session");
        }

        const session = data?.data ?? data ?? {};
        const userId = localStorage.getItem("current_user_swimmer_id") ?? "";
        const paymentStatus =
          session.payment_status ||
          session.status ||
          session.checkout_session_status;
        const isPaid =
          paymentStatus === "paid" ||
          paymentStatus === "complete" ||
          paymentStatus === "completed" ||
          paymentStatus === "succeeded";

        if (isPaid) {
          if (userId) {
            await stripeRepository.saveSubscription({
              user_id: String(userId),
              session_id: String(sessionId),
            });
          }

          setStatus("success");
          toast.success("Subscription verified");

          storageService.setItem("pro_subscription_active", "true");
          storageService.setItem("subscription_status", String(paymentStatus || "active"));
          if (session.current_period_end) {
            storageService.setItem("subscription_expires_at", String(session.current_period_end));
          }
          if (session.billing_cycle) {
            storageService.setItem("subscription_interval", String(session.billing_cycle));
          }
          secureStorage.remove("stripe_checkout_session_id");
        } else {
          setStatus("failed");
          secureStorage.remove("stripe_checkout_session_id");
          toast.error(canceled ? "Payment was canceled." : "Subscription is not active");
        }
      } catch (error) {
        logger.error("Subscription verification failed", error as Error, { sessionId });
        setStatus("failed");
        toast.error("Unable to verify subscription");
      }
    };

    verifySubscription();
  }, [canceled, logger, stripeRepository]);

  const handleConfirm = () => {
    onFinish("home");
  };

  const handleRetry = () => {
    onFinish();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-50 p-6 z-[99999]">
      <div className="bg-white dark:bg-gray-900 shadow p-8 rounded-lg max-w-md w-full text-center">
        {status === null && (
          <p className="text-gray-600 dark:text-gray-300">
            Checking payment status...
          </p>
        )}

        {status === "success" && (
          <>
            <h1 className="text-2xl font-semibold mb-4 dark:text-white">
              Payment Successful
            </h1>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Your subscription is now active.
            </p>

            <button
              onClick={handleConfirm}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
            >
              Continue
            </button>
          </>
        )}

        {status === "failed" && (
          <>
            <h1 className="text-2xl font-semibold mb-4 dark:text-white">
              Payment Failed
            </h1>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Payment was not completed or subscription could not be verified.
            </p>

            <button
              onClick={handleRetry}
              className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold"
            >
              Return
            </button>
          </>
        )}
      </div>
    </div>
  );
}
